import type { Bounds } from 'quill/core';
import Quill, { Delta } from 'quill/core';
import type { ReactNode } from 'react';
import React, { useEffect, useMemo } from 'react';
import {
  autoUpdate,
  flip,
  hide,
  offset,
  useFloating,
} from '@floating-ui/react-dom';
import { createPortal } from 'react-dom';
import { usePrefixListener } from '../PrefixListener';
import styles from './EmojiAutocompleter.module.scss';
import EmojiPicker from '../../components/EmojiPicker';
import addTestId from '../../utils/addTestId';

const USE_PREFIX_LISTENER_OPTIONS = [{ prefix: ':', isAllowedInTitle: true }];

interface ContainerProps {
  children: ReactNode;
  bounds: Bounds;
}

const Container = ({ children, bounds }: ContainerProps) => {
  const middleware = useMemo(
    () => [
      offset(6),
      hide(),
      flip({
        fallbackStrategy: 'initialPlacement',
      }),
    ],
    [],
  );

  const { refs, floatingStyles, middlewareData } = useFloating({
    placement: 'bottom-start',
    middleware,
    whileElementsMounted: autoUpdate,
  });

  return (
    <>
      {createPortal(
        <div
          className={styles.pickerContainer}
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            visibility: middlewareData.hide?.referenceHidden
              ? 'hidden'
              : 'visible',
          }}
          {...addTestId('emojiAutocompleter')}
        >
          {children}
        </div>,
        document.body,
      )}
      <div
        ref={refs.setReference}
        style={{
          position: 'absolute',
          pointerEvents: 'none',
          left: bounds.left,
          top: bounds.top,
          height: bounds.height,
        }}
      />
    </>
  );
};

interface EmojiAutocompleterProps {
  quill: Quill;
  readOnly: boolean;
}

const EmojiAutocompleter = ({ quill, readOnly }: EmojiAutocompleterProps) => {
  const { bounds, text, prefix, reset } = usePrefixListener(
    quill,
    USE_PREFIX_LISTENER_OPTIONS,
  );
  const query = text ? text.toLowerCase() : '';
  const isShowingMenu = !!bounds && !readOnly;

  useEffect(() => {
    if (/^\s/.test(query)) {
      reset?.();
    }
  }, [query, reset]);

  return (
    isShowingMenu && (
      <Container bounds={bounds}>
        <EmojiPicker
          inputContainer={quill.root}
          query={query}
          onPick={(emoji) => {
            reset?.();

            const range = quill.getSelection(true);
            if (range == null || text == null || prefix == null) return;
            const deletionLength = text.length + prefix.length;
            const index = range.index - deletionLength;
            quill.updateContents(
              new Delta().retain(index).delete(deletionLength).insert(emoji),
              Quill.sources.USER,
            );
            quill.setSelection(index + emoji.length, Quill.sources.USER);
          }}
          onEscapePress={() => reset?.()}
          onClearQuery={() => {
            const range = quill.getSelection(true);
            if (range == null || text == null || prefix == null) return;
            const deletionLength = text.length;
            const index = range.index - deletionLength;
            quill.updateContents(
              new Delta().retain(index).delete(deletionLength),
              Quill.sources.USER,
            );
            quill.setSelection(index, Quill.sources.USER);
          }}
        />
      </Container>
    )
  );
};

export default EmojiAutocompleter;
